// src/pages/RolesPage.js
import React, { useContext, useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Tooltip, Checkbox, Chip, Alert, List, ListItemButton,
  ListItemText, FormControlLabel, Switch
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Security as SecurityIcon
} from '@mui/icons-material';
import { ApiContext } from '../contexts/ApiContext';
import { useNotification } from '../contexts/NotificationContext';
import { usePermissions } from '../hooks/usePermissions';

// Nombres amigables para los módulos
const MODULE_LABELS = {
  dashboard: 'Mi Escritorio',
  'agent-calls': 'Mis Llamadas',
  profile: 'Mi Perfil',
  agents: 'Agentes',
  skills: 'Skills / Colas',
  'skill-assignment': 'Asignación de Skills',
  extensions: 'Extensiones',
  trunks: 'Troncales',
  amd: 'AMD',
  campaigns: 'Campañas',
  datastructures: 'Estructuras de Datos',
  dialplans: 'Planes de Marcado',
  loads: 'Cargas',
  'engine-status': 'Estado del Motor',
  ends: 'Finales',
  reports: 'Reportes',
  settings: 'Configuración del Sistema',
  roles: 'Roles y Permisos',
  supervisor: 'Supervisor de Agentes',
  'monitoring-center': 'Centro de Monitoreo',
  'skill-supervisor': 'Supervisión de Skills',
  'skill-monitor': 'Monitor de Skills',
  'queue-supervisor': 'Supervisión de Colas',
  'call-logs': 'Registros de Llamadas',
  'call-history': 'Historial de Llamadas',
  workflows: 'Workflows',
  'crm-configs': 'CRM',
  simulator: 'Simulador',
  webhooks: 'Webhooks',
  multimedia: 'Multimedia',
  'agent-tracking': 'Tracking de Agentes',
  'agent-tags': 'Etiquetas de Agentes',
  'api-tokens': 'API Tokens',
  wall: 'Muro',
  chat: 'Chat',
};

// Categorías de módulos
const MODULE_CATEGORIES = [
  { key: 'general', label: 'General', modules: ['dashboard', 'agent-calls', 'profile', 'wall', 'chat'] },
  { key: 'admin', label: 'Administración', modules: ['agents', 'skills', 'skill-assignment', 'extensions', 'trunks', 'amd', 'campaigns', 'datastructures', 'dialplans', 'loads', 'ends', 'simulator', 'workflows', 'api-tokens', 'webhooks', 'multimedia'] },
  { key: 'supervision', label: 'Supervisión y Reportes', modules: ['engine-status', 'supervisor', 'monitoring-center', 'skill-supervisor', 'skill-monitor', 'queue-supervisor', 'call-logs', 'reports'] },
  { key: 'config', label: 'Configuración', modules: ['roles', 'settings'] },
];

export default function RolesPage() {
  const { roles } = useContext(ApiContext);
  const { showSuccess, showError } = useNotification();
  const { canAction } = usePermissions();

  const [roleList, setRoleList] = useState([]);
  const [allPermissions, setAllPermissions] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formPerms, setFormPerms] = useState({});
  const [activeCategory, setActiveCategory] = useState('general');
  const [saving, setSaving] = useState(false);

  const loadRoles = async () => {
    try {
      const r = await roles.getAll();
      setRoleList(r?.data?.roles ?? []);
    } catch (e) {
      console.error(e);
      showError('Error al cargar roles');
    }
  };

  const loadPermissions = async () => {
    try {
      const r = await roles.getPermissions();
      setAllPermissions(r?.data?.permissionsByModule ?? {});
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadRoles();
    loadPermissions();
  }, []);

  // Categorías con módulos disponibles
  const categorizedModules = useMemo(() => {
    const availableModules = Object.keys(allPermissions);
    const categorized = MODULE_CATEGORIES.map(cat => ({
      ...cat,
      modules: cat.modules.filter(m => availableModules.includes(m)),
    })).filter(cat => cat.modules.length > 0);

    return categorized;
  }, [allPermissions]);

  // Categoría activa
  const currentCategory = useMemo(() => {
    return categorizedModules.find(c => c.key === activeCategory) || categorizedModules[0];
  }, [categorizedModules, activeCategory]);

  // Contar permisos activos por categoría (para el badge)
  const countCategoryPerms = (cat) => {
    if (!cat) return 0;
    let count = 0;
    cat.modules.forEach(mod => {
      const modulePerms = allPermissions[mod] || [];
      modulePerms.forEach(p => {
        if (formPerms[p.id]) count++;
      });
    });
    return count;
  };

  const openDialog = (role = null) => {
    setEditing(role);
    setFormName(role?.role_name || '');
    setFormDesc(role?.description || '');

    // Cargar permisos: enabled = can_view (flag único)
    const perms = {};
    if (role?.permissionsByModule) {
      Object.values(role.permissionsByModule).flat().forEach(p => {
        perms[p.id] = p.can_view || false;
      });
    }
    setFormPerms(perms);
    setActiveCategory(categorizedModules[0]?.key || 'general');
    setDialogOpen(true);
  };

  // Toggle un permiso individual (habilitado/deshabilitado)
  const togglePerm = (permId) => {
    setFormPerms(prev => ({ ...prev, [permId]: !prev[permId] }));
  };

  // Toggle todos los permisos de la categoría activa
  const toggleCategoryAll = () => {
    if (!currentCategory) return;
    const allModulePerms = currentCategory.modules.flatMap(mod => allPermissions[mod] || []);
    const allChecked = allModulePerms.every(p => formPerms[p.id]);
    setFormPerms(prev => {
      const next = { ...prev };
      allModulePerms.forEach(p => { next[p.id] = !allChecked; });
      return next;
    });
  };

  const isCategoryAllChecked = () => {
    if (!currentCategory) return false;
    const allModulePerms = currentCategory.modules.flatMap(mod => allPermissions[mod] || []);
    if (allModulePerms.length === 0) return false;
    return allModulePerms.every(p => formPerms[p.id]);
  };
  const isCategoryIndeterminate = () => {
    if (!currentCategory) return false;
    const allModulePerms = currentCategory.modules.flatMap(mod => allPermissions[mod] || []);
    const someChecked = allModulePerms.some(p => formPerms[p.id]);
    return someChecked && !isCategoryAllChecked();
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      showError('El nombre del rol es requerido');
      return;
    }
    setSaving(true);
    try {
      const permissions = [];
      Object.entries(formPerms).forEach(([permId, enabled]) => {
        if (enabled) {
          permissions.push({
            permission_id: parseInt(permId),
            can_view: true,
            can_create: true,
            can_edit: true,
            can_delete: true,
          });
        }
      });
      const payload = { role_name: formName.trim(), description: formDesc.trim(), permissions };

      if (editing) {
        await roles.update(editing.id, payload);
        showSuccess('Rol actualizado');
      } else {
        await roles.create(payload);
        showSuccess('Rol creado');
      }
      setDialogOpen(false);
      loadRoles();
    } catch (e) {
      console.error(e);
      showError(e?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role) => {
    if (['SuperAdmin', 'Admin'].includes(role.role_name)) {
      showError('No se pueden eliminar roles del sistema');
      return;
    }
    if (!window.confirm(`¿Eliminar rol "${role.role_name}"?`)) return;
    try {
      await roles.delete(role.id);
      showSuccess('Rol eliminado');
      loadRoles();
    } catch (e) {
      console.error(e);
      showError(e?.message || 'Error al eliminar');
    }
  };

  const countPerms = (role) => {
    if (!role.permissionsByModule) return 0;
    let count = 0;
    Object.values(role.permissionsByModule).flat().forEach(p => {
      if (p.can_view) count++;
    });
    return count;
  };

  const isSystemRole = (name) => ['SuperAdmin', 'Admin'].includes(name);

  return (
    <Box p={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={600}>Roles y Permisos</Typography>
        {canAction('create_role') && (
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => openDialog(null)}>
            Nuevo Rol
          </Button>
        )}
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="center">
            <SecurityIcon fontSize="small" />
            <Typography variant="body2">Total roles: {roleList.length}</Typography>
          </Box>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell align="center">Permisos</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roleList.map((role) => (
              <TableRow key={role.id} hover>
                <TableCell>{role.id}</TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    {role.role_name}
                    {isSystemRole(role.role_name) && (
                      <Chip label="Sistema" size="small" color="primary" variant="outlined" />
                    )}
                  </Box>
                </TableCell>
                <TableCell>{role.description || '-'}</TableCell>
                <TableCell align="center">{countPerms(role)}</TableCell>
                <TableCell align="center">
                  <Chip label={role.is_active ? 'Activo' : 'Inactivo'} size="small" color={role.is_active ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="right">
                  {canAction('edit_role') && (
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => openDialog(role)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                  {canAction('delete_role') && !isSystemRole(role.role_name) && (
                    <Tooltip title="Eliminar">
                      <IconButton size="small" color="error" onClick={() => handleDelete(role)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {roleList.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center">No hay roles</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de crear/editar rol con Tabs laterales */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? `Editar Rol: ${editing.role_name}` : 'Nuevo Rol'}</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {editing && isSystemRole(editing.role_name) && (
            <Alert severity="info" sx={{ m: 2, mb: 0 }}>
              Este es un rol del sistema. Puede editar los permisos pero no el nombre.
            </Alert>
          )}

          {/* Campos nombre y descripción */}
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} p={2} pb={1}>
            <TextField
              label="Nombre del Rol"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              disabled={editing && isSystemRole(editing.role_name)}
              required
              size="small"
            />
            <TextField
              label="Descripción"
              value={formDesc}
              onChange={(e) => setFormDesc(e.target.value)}
              size="small"
            />
          </Box>

          <Typography variant="subtitle2" fontWeight={600} px={2} pt={1} pb={0.5} color="text.secondary">
            Matriz de Permisos
          </Typography>

          {/* Layout de tabs laterales + tabla */}
          <Box display="flex" sx={{ height: 420, borderTop: '1px solid', borderColor: 'divider' }}>
            {/* Sidebar de categorías */}
            <Box sx={{
              width: 200,
              minWidth: 200,
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
              overflowY: 'auto',
            }}>
              <List disablePadding>
                {categorizedModules.map((cat) => {
                  const isActive = activeCategory === cat.key;
                  const permCount = countCategoryPerms(cat);
                  return (
                    <ListItemButton
                      key={cat.key}
                      selected={isActive}
                      onClick={() => setActiveCategory(cat.key)}
                      sx={{
                        py: 1.2,
                        px: 2,
                        borderLeft: isActive ? '3px solid' : '3px solid transparent',
                        borderColor: isActive ? 'primary.main' : 'transparent',
                        '&.Mui-selected': { bgcolor: 'background.paper' },
                        '&.Mui-selected:hover': { bgcolor: 'background.paper' },
                      }}
                    >
                      <ListItemText
                        primary={cat.label}
                        primaryTypographyProps={{
                          variant: 'body2',
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? 'primary.main' : 'text.primary',
                        }}
                      />
                      <Chip
                        label={permCount}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          bgcolor: permCount > 0 ? 'primary.50' : 'grey.200',
                          color: permCount > 0 ? 'primary.main' : 'text.secondary',
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>

            {/* Contenido: tabla de permisos de la categoría activa */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Barra superior: activar/desactivar todo */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 0.5,
                bgcolor: 'grey.50',
                borderBottom: '1px solid',
                borderColor: 'divider',
                minHeight: 40,
              }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={isCategoryAllChecked()}
                      onChange={toggleCategoryAll}
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={500}>
                      {isCategoryAllChecked() ? 'Desactivar todos' : 'Activar todos'}
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />
              </Box>

              {/* Lista de permisos agrupados por módulo */}
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {currentCategory?.modules.map((moduleName) => {
                  const modulePerms = allPermissions[moduleName] || [];
                  const label = MODULE_LABELS[moduleName] || moduleName;
                  const enabledCount = modulePerms.filter(p => formPerms[p.id]).length;

                  return (
                    <Box key={moduleName} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                      {/* Encabezado del módulo */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 2,
                        py: 1,
                        bgcolor: 'action.hover',
                      }}>
                        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                          {label}
                        </Typography>
                        <Chip
                          label={`${enabledCount}/${modulePerms.length}`}
                          size="small"
                          color={enabledCount === modulePerms.length ? 'success' : enabledCount > 0 ? 'warning' : 'default'}
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>

                      {/* Switches de permisos */}
                      <Box sx={{ px: 1, py: 0.5 }}>
                        {modulePerms.map((perm) => (
                          <Box
                            key={perm.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              px: 1,
                              py: 0.3,
                              borderRadius: 1,
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <Typography variant="body2" color={formPerms[perm.id] ? 'text.primary' : 'text.secondary'}>
                              {perm.description || perm.permission_name}
                            </Typography>
                            <Switch
                              size="small"
                              checked={!!formPerms[perm.id]}
                              onChange={() => togglePerm(perm.id)}
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : (editing ? 'Guardar' : 'Crear')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
